import { Button } from 'primereact/button';
import { useState, useRef, useEffect } from 'react';
import { CustomFileUpload } from './CustomFileUpload';
import { Toast } from 'primereact/toast';
import { fetchAccessToken, apiCallErrorHandler } from '../utils/common';
import { useAuth0 } from '@auth0/auth0-react';
import { ProgressSpinner } from 'primereact/progressspinner';
import { downloadCompressed, discardSessionRequest, generateSessionId } from '../utils/pdfUtils';
import { Dropdown } from 'primereact/dropdown';
import { Dialog } from 'primereact/dialog';
import { ProgressBar } from 'primereact/progressbar';

export const PdfDownloaderForm = () => {
  const { getAccessTokenSilently } = useAuth0();
  useEffect(() => { document.title = 'OptiConfig PDF Downloader'; }, []);

  const toast = useRef(null);

  const showError = (err) => {
    toast.current?.show({ severity: 'error', summary: 'Error', detail: err, life: 3000 });
  };

  const showSuccess = (msg) => {
    toast.current?.show({ severity: 'success', summary: 'Success', detail: msg, life: 3000 });
  };

  const [token, setToken] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [sessionId, setSessionId] = useState(generateSessionId());
  const [succeeded, setSucceeded] = useState(false);

  const [worksheet, setWorksheet] = useState('');
  const [worksheetOptions, setWorksheetOptions] = useState([]);
  const [worksheetTouched, setWorksheetTouched] = useState(false);

  const [progress, setProgress] = useState({ processed: 0, total: 0 });
  const [isProcessing, setIsProcessing] = useState(false);

  const [dialogData, setDialogData] = useState(null);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [errorKeys, setErrorKeys] = useState([]);

  useEffect(() => {
    const fetchToken = async () => {
      try {
        const accessToken = await fetchAccessToken(getAccessTokenSilently);
        setToken(accessToken);
      } catch (error) {
        console.error('Error fetching access token:', error);
        showError('Failed to fetch access token. Please try again.');
      }
    };
    fetchToken();
  }, [getAccessTokenSilently]);

  const validate = () => {
    if (!uploadedFiles || uploadedFiles.length !== 1) {
      showError('Please upload exactly one Excel file.');
      return false;
    }
    if (!worksheet || !worksheet.trim()) {
      setWorksheetTouched(true);
      showError('Please select a worksheet.');
      return false;
    }
    return true;
  };

  const buildFileData = () =>
    uploadedFiles.map(file => ({
      _relativePath: file._relativePath,
      lastModified: file.lastModified,
      lastModifiedDate: file.lastModifiedDate,
      name: file.name,
      size: file.size,
      type: file.type,
      webkitRelativePath: file.webkitRelativePath
    }));

  const postParseFile = async (doTest = false) => {
    const payload = {
      fileName: uploadedFiles[0].name,
      worksheet,
      uploadedFiles: buildFileData(),
      doTest
    };

    const response = await fetch(
      `${process.env.REACT_APP_API_URL || ''}/file/parse-file/${sessionId}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      }
    );

    if (!response.ok) await apiCallErrorHandler(response, doTest ? 'Failed to parse file (test)' : 'Failed to parse file');

    const raw = await response.json();
    const normalized = {
      mapping: raw.mapping || raw.Mapping || {},
      rowCount: raw.rowCount || raw.RowCount || 0,
      preview: raw.preview || raw.Preview || {}
    };
    return normalized;
  };

  const postDownloadReports = async (rowsChunkObj, chunkIndex) => {
    const response = await fetch(
      `${process.env.REACT_APP_API_URL || ''}/file/download-reports/${sessionId}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          worksheet,
          chunkIndex,
          rows: rowsChunkObj
        })
      }
    );

    if (!response.ok) await apiCallErrorHandler(response, 'Failed to process report chunk');

    // Expecting array of successfully downloaded keys, e.g. ["E0251MYT", ...]
    const successKeys = await response.json().catch(() => []);
    const requestedKeys = Object.keys(rowsChunkObj || {});
    const missing = requestedKeys.filter(k => !successKeys.includes(k));
    if (missing.length) {
      setErrorKeys(prev => [...prev, ...missing]);
    }
    return successKeys;
  };

  const handleSubmit = async (doTest = false) => {
    if (!validate()) return;

    setSubmitting(true);
    setIsProcessing(false);
    setProgress({ processed: 0, total: 0 });
    setErrorKeys([]);

    try {
      const data = await postParseFile(doTest);
      setDialogData(data);
      setDialogVisible(true);

      const entries = Object.entries(data.preview || {});
      const totalRows = entries.length;
      const chunkSize = 3;

      setIsProcessing(true);
      setProgress({ processed: 0, total: totalRows });

      let totalSuccessCount = 0;

      for (let i = 0; i < totalRows; i += chunkSize) {
        const chunkEntries = entries.slice(i, i + chunkSize);
        const chunkObj = Object.fromEntries(chunkEntries);
        const successKeys = await postDownloadReports(chunkObj, i / chunkSize);
        totalSuccessCount += (successKeys || []).length;

        setProgress(prev => ({
          processed: Math.min(prev.processed + chunkEntries.length, prev.total),
          total: prev.total
        }));
      }

      setIsProcessing(false);

      if (totalRows > 0 && totalSuccessCount === 0) {
        // Every row failed to process - there is nothing to download.
        setSucceeded(false);
        showError('All rows failed to process. See errors for details.');
      } else {
        setSucceeded(true);
        showSuccess(doTest ? 'Test run completed successfully' : 'Reports generated successfully');
      }
    } catch (error) {
      setIsProcessing(false);
      showError(error.message || (doTest ? 'Test submission failed.' : 'Submission failed.'));
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    await handleSubmit(false);
  };

  const onTest = async (e) => {
    e.preventDefault();
    await handleSubmit(true);
  };

  const discardSession = async (e) => {
    e?.preventDefault();
    try {
      await discardSessionRequest(sessionId, token);
    } catch (err) {
      showError(err.message || 'Failed to discard session on server.');
    } finally {
      setUploadedFiles([]);
      setSucceeded(false);
      setSessionId(generateSessionId());
    }
  };

  const onDownload = async (e) => {
    e.preventDefault();

    setSubmitting(true);
    try {
      await downloadCompressed(sessionId, token);
      showSuccess('File downloaded successfully');
      await discardSession();
    } catch (error) {
      showError(error.message || 'Download failed.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className="grid" style={{ maxWidth: '100vw', overflowX: 'auto' }} onSubmit={onSubmit}>
      <Toast ref={toast} />
      <Dialog
        header="Download Report"
        visible={dialogVisible}
        style={{ width: '75vw', maxWidth: '1200px' }}
        onHide={() => setDialogVisible(false)}
      >
        {dialogData ? (
          <div className="p-fluid" style={{ fontSize: '0.8rem', lineHeight: 1.25 }}>
            <div className="field">
              <strong>Total Rows:</strong> {dialogData.rowCount}
            </div>
            <div className="field">
              <strong>Mapping:</strong>
              <ul style={{ maxHeight: 240, overflowY: 'auto', paddingLeft: 16, fontSize: '0.75rem' }}>
                {Object.entries(dialogData.mapping || {}).map(([k, v]) => (
                  <li key={`map-${k}`}>
                    <strong>{k}:</strong> {v}
                  </li>
                ))}
              </ul>
            </div>
            <div className="field">
              <strong>Preview (top 3 rows):</strong>
              <ul style={{ maxHeight: 300, overflowY: 'auto', paddingLeft: 16, fontSize: '0.75rem' }}>
                {Array.isArray(dialogData.preview)
                  ? dialogData.preview.slice(0, 3).map((row, idx) => (
                      <li key={`prev-row-${idx}`}>
                        <span style={{ wordBreak: 'break-all' }}>
                          {typeof row === 'object' ? JSON.stringify(row) : String(row)}
                        </span>
                      </li>
                    ))
                  : Object.entries(dialogData.preview || {}).slice(0, 3).map(([plate, url]) => (
                      <li key={`prev-${plate}`}>
                        <strong>{plate}:</strong> <span style={{ wordBreak: 'break-all' }}>{String(url)}</span>
                      </li>
                    ))
                }
              </ul>
            </div>
            <div className="field">
              <strong>Download Progress:</strong>
              <div style={{ marginTop: 8 }}>
                <ProgressBar
                  value={
                    dialogData && progress.total > 0
                      ? Math.round((progress.processed / progress.total) * 100)
                      : 0
                  }
                />
                <div style={{ fontSize: '0.75rem', marginTop: 4 }}>
                  {isProcessing
                    ? `Processing ${progress.processed} / ${progress.total} rows...`
                    : progress.total > 0
                      ? `Processed ${progress.processed} / ${progress.total} rows`
                      : 'Idle'}
                </div>
              </div>
            </div>
            {errorKeys.length > 0 && (
              <div className="field">
                <strong>Errors:</strong>
                <ul style={{ maxHeight: 200, overflowY: 'auto', paddingLeft: 16, fontSize: '0.75rem', color: '#b00020' }}>
                  {errorKeys.map((plate) => (
                    <li key={`err-${plate}`}>{plate}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ) : (
          <div style={{ fontSize: '0.8rem' }}>No data</div>
        )}
      </Dialog>
      <div className="col-12">
        <div className="grid formgrid p-fluid">
          <div className="field col-12">
            <h4 style={{ textAlign: 'center' }}>Appraisal Report Downloader Tool</h4>
          </div>
          <div className="field col-4"></div>
          <div className="field col-4" style={{ display: 'flex', justifyContent: 'center', gap: 12 }}>
            {!succeeded && (
              <>
                <Button
                  type="button"
                  label={submitting ? 'Testing...' : 'Test (top 3)'}
                  className="p-button-secondary"
                  style={{ maxWidth: '300px', width: '100%' }}
                  disabled={uploadedFiles.length === 0 || submitting || !worksheet}
                  onClick={onTest}
                />
                <Button
                  type="submit"
                  label={submitting ? 'Submitting...' : 'Submit'}
                  className="p-button-primary"
                  style={{ maxWidth: '300px', width: '100%' }}
                  disabled={uploadedFiles.length === 0 || submitting || !worksheet}
                />
              </>
            )}
            {succeeded && (
              <>
                <Button
                  onClick={onDownload}
                  label={submitting ? 'Downloading...' : 'Download .zip'}
                  className="p-button-primary"
                  style={{ width: '50%' }}
                  disabled={uploadedFiles.length === 0 || submitting || isProcessing}
                />
              </>
            )}
            {uploadedFiles.length > 0 && (
              <Button
                onClick={discardSession}
                label={'Discard'}
                className="p-button-secondary"
                style={{ width: '50%', marginLeft: 20 }}
                disabled={submitting || isProcessing}
              />
            )}
          </div>
          <div className="field col-4">
            {submitting && <ProgressSpinner style={{ width: 30, height: 30 }} strokeWidth="4" />}
          </div>

          <div className="field col-3"></div>
          <div className="field col-6" style={{ paddingLeft: '1rem', paddingRight: '1rem' }}>
            <CustomFileUpload
              allowMultiple={false}
              acceptedFileTypes={[
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                'application/vnd.ms-excel',
                '.xlsx',
                '.xls'
              ]}
              onMessage={(msg) => { showError(msg.detail); }}
              token={token}
              fileValue={uploadedFiles}
              sessionId={sessionId}
              onChange={(value) => {
                const file = JSON.parse(value);
                if (file.extraInfo && Array.isArray(file.extraInfo)) {
                  const wsArr = file.extraInfo;
                  setWorksheetOptions(wsArr.map(ws => ({ label: ws, value: ws })));
                  if (wsArr.length === 1) setWorksheet(wsArr[0]);
                  else setWorksheet('');
                } else {
                  setWorksheet('');
                  setWorksheetOptions([]);
                }
              }}
              onChangeFiles={(files) => {
                setSucceeded(false);
                setUploadedFiles(files);
                if (files.length === 0) {
                  setWorksheet('');
                  setWorksheetOptions([]);
                }
              }}
            />
          </div>
          <div className="field col-3"></div>

          <div className="field col-3"></div>
          <div className={`field col-6${worksheetTouched && !worksheet ? ' p-invalid' : ''}`}>
            <label htmlFor="worksheet">Worksheet</label>
            <Dropdown
              id="worksheet"
              value={worksheet}
              options={worksheetOptions}
              onChange={e => setWorksheet(e.value)}
              onBlur={() => setWorksheetTouched(true)}
              className={`w-full${worksheetTouched && !worksheet ? ' p-invalid' : ''}`}
              placeholder="Select worksheet"
              disabled={worksheetOptions.length === 1}
            />
            {worksheetTouched && !worksheet && (
              <small className="p-error">Worksheet is required.</small>
            )}
          </div>

          <div className="field col-3"></div>
        </div>
      </div>
    </form>
  );
};
